import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Radio, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';

export default function ZohoTestWebhook({ form, onTestComplete }) {
  const [testing, setTesting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [result, setResult] = useState(null); // 'success' | 'timeout' | null
  const intervalRef = useRef(null);
  const pollRef = useRef(null);

  const cleanup = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
    intervalRef.current = null;
    pollRef.current = null;
  };

  useEffect(() => { return cleanup; }, []);

  const startTest = async () => {
    setTesting(true);
    setResult(null);
    setCountdown(60);

    try {
      await zohoAPI.startTest(form.id);
      toast.info('Test mode started — send a submission from Zoho within 60 seconds');

      // Countdown timer
      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            cleanup();
            setTesting(false);
            setResult('timeout');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Poll for test status every 3 seconds
      pollRef.current = setInterval(async () => {
        try {
          const res = await zohoAPI.checkTestStatus(form.id);
          if (res.data.status === 'success') {
            cleanup();
            setTesting(false);
            setResult('success');
            toast.success('Test submission received! Fields detected.');
            onTestComplete();
          } else if (res.data.status === 'timeout') {
            cleanup();
            setTesting(false);
            setResult('timeout');
          }
        } catch {
          // ignore poll errors
        }
      }, 3000);
    } catch (e) {
      setTesting(false);
      toast.error('Failed to start test mode');
    }
  };

  const stopTest = async () => {
    cleanup();
    setTesting(false);
    setCountdown(0);
    try {
      await zohoAPI.stopTest(form.id);
      toast.info('Test mode stopped');
    } catch {}
  };

  // Don't show if form already has detected fields and is past pending_test
  const showTestSection = form.status === 'pending_test' || form.detected_fields?.length === 0 || testing || result;

  if (!showTestSection) return null;

  return (
    <div className="border rounded-md p-3 bg-muted/30 space-y-2" data-testid="test-webhook-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Test Webhook</span>
          {testing && (
            <Badge variant="outline" className="gap-1 text-xs animate-pulse">
              <Clock className="h-3 w-3" />
              Listening... {countdown}s
            </Badge>
          )}
          {result === 'success' && (
            <Badge variant="default" className="gap-1 text-xs">
              <CheckCircle2 className="h-3 w-3" />Fields Detected
            </Badge>
          )}
          {result === 'timeout' && (
            <Badge variant="destructive" className="gap-1 text-xs">
              <XCircle className="h-3 w-3" />Timed Out
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          {!testing ? (
            <Button size="sm" variant={result === 'timeout' ? 'default' : 'outline'} onClick={startTest} data-testid="start-test-btn">
              {result === 'timeout' ? 'Retry Test' : 'Start Test'}
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={stopTest} data-testid="stop-test-btn">
              <XCircle className="h-4 w-4 mr-1" />Stop
            </Button>
          )}
        </div>
      </div>

      {testing && (
        <div className="space-y-2">
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all duration-1000"
              style={{ width: `${(countdown / 60) * 100}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Send a test submission from your Zoho form now. The webhook URL is shown above.
          </p>
        </div>
      )}

      {result === 'timeout' && (
        <p className="text-xs text-muted-foreground">
          No submission received within 60 seconds. Make sure the webhook URL is correctly configured in Zoho.
        </p>
      )}

      {result === 'success' && (
        <p className="text-xs text-muted-foreground">
          Fields have been detected from your test submission. You can now configure field mappings below.
        </p>
      )}
    </div>
  );
}
